import type { HttpResponse } from '@brickflow/http'
import type { Ref } from 'vue'

import { httpErrorMapper, type HttpErrorMapperKeys } from './http-error'

export function createResponseInterceptor(
  wasHydration: Readonly<Ref<boolean>>,
  runWithContext: (callback: () => void) => void,
): (response: HttpResponse) => void {
  return (response) => {
    const normalizedResponse = response as HttpResponse
    const status = normalizedResponse.status
    const config = normalizedResponse.config

    if (
      !httpErrorMapper[status as HttpErrorMapperKeys] ||
      config.ignore?.(
        normalizedResponse as HttpResponse<Record<string, unknown> & { error_message?: string; status?: string }>,
      )
    ) {
      return
    }

    if (status === 200 && !isErrorPayload(normalizedResponse.data)) {
      return
    }

    const resp = normalizedResponse as HttpResponse<{ error_message: string; params: string; status: 'error' }>

    if (import.meta.client && window?.dataLayer) {
      const startHref = window.location.href

      setTimeout(() => {
        if (startHref !== window.location.href) {
          return
        }

        window.dataLayer?.push?.({
          event: 'client-error',
          eventModel: {
            error_text: resp.data.error_message,
            name: resp.config.url,
            req_body: '',
            req_query: JSON.stringify(config.params),
          },
        })
      }, 3000)
    }

    if (import.meta.client) {
      handleError(httpErrorMapper[status as HttpErrorMapperKeys].bind(null, resp), wasHydration, runWithContext)
    }
  }
}

function handleError(
  callback: () => void,
  wasHydration: Readonly<Ref<boolean>>,
  runWithContext: (callback: () => void) => void,
): void {
  if (wasHydration.value) {
    setTimeout(() => {
      runWithContext(callback)
    }, 100)
    return
  }

  requestAnimationFrame(() => handleError(callback, wasHydration, runWithContext))
}

function isErrorPayload(payload: unknown): payload is { error_message: string; params: string; status: 'error' } {
  return Boolean(
    payload &&
    typeof payload === 'object' &&
    'status' in payload &&
    (payload as { status?: string }).status === 'error' &&
    'error_message' in payload,
  )
}
